import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import type { AuthContextValue, AuthUser } from '../domain/session';
import { hasPermission as userHasPermission } from '../domain/session.rules';
import { authRepository } from '../infrastructure/auth.repository';
import {
  clearSession,
  persistSession,
  persistUser,
  readAccessToken,
  readStoredUser,
} from '../infrastructure/sessionStorage';
import { AuthContext } from './useAuth';
import useIdleTimeout from './useIdleTimeout';

export function AuthProvider({ children }: { children: ReactNode }) {
  const { i18n, t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [user, setUser] = useState<AuthUser | null>(readStoredUser);
  const [loading, setLoading] = useState(() => Boolean(readAccessToken()) && !readStoredUser());

  const applyLanguage = useCallback(
    (userData: AuthUser | null | undefined) => {
      if (userData?.preferredLanguage) {
        const language = userData.preferredLanguage;
        void i18n.changeLanguage(language);
        localStorage.setItem('language', language);
      }
    },
    [i18n]
  );

  useEffect(() => {
    const token = readAccessToken();
    if (token && !user) {
      authRepository
        .getMe()
        .then((userData) => {
          setUser(userData);
          persistUser(userData);
          applyLanguage(userData);
        })
        .catch(() => {
          clearSession();
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else if (user) {
      applyLanguage(user);
    }
    // Preserve the characterized mount-only session verification.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(
    async (username: string, password: string) => {
      const session = await authRepository.login(username, password);
      persistSession(session);
      setUser(session.user);
      applyLanguage(session.user);
      return session;
    },
    [applyLanguage]
  );

  const logout = useCallback(() => {
    clearSession();
    setUser(null);
  }, []);

  const handleIdleLogout = useCallback(() => {
    logout();
    enqueueSnackbar(t('auth.sessionExpired', 'Su sesión ha expirado por inactividad'), {
      variant: 'warning',
      autoHideDuration: 6000,
    });
  }, [logout, enqueueSnackbar, t]);

  useIdleTimeout(handleIdleLogout, Boolean(user));

  const hasPermission = useCallback(
    (permissionKey: string) => userHasPermission(user, permissionKey),
    [user]
  );

  const refreshUser = useCallback(async () => {
    try {
      const userData = await authRepository.getMe();
      setUser(userData);
      persistUser(userData);
      applyLanguage(userData);
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  }, [applyLanguage]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      login,
      logout,
      hasPermission,
      refreshUser,
      isAuthenticated: Boolean(user),
    }),
    [user, loading, login, logout, hasPermission, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}